import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onNotify?: (type: 'success' | 'error', message: string) => void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose, onSuccess, onNotify }) => {
  const { user } = useSupabaseAuth();
  const [loading, setLoading] = useState(false);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [form, setForm] = useState({
    email: '',
    full_name: '',
    phone_number: ''
  });

  useEffect(() => {
    const fetchCustomer = async () => {
      if (!user?.id) return;
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('customer_id, company_name')
        .eq('id', user.id)
        .single();
      setCustomerId(profile?.customer_id || null);
      setCompanyName(profile?.company_name || null);
    };
    if (isOpen) fetchCustomer();
  }, [isOpen, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId) {
      onNotify?.('error', 'Your account is not assigned to a customer.');
      return;
    }

    try {
      setLoading(true);

      // Temp password (unused; real password set via email link)
      const tempPassword = crypto.randomUUID() + 'Aa1!';

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: form.email,
        password: tempPassword,
        options: {
          data: {
            full_name: form.full_name,
            customer_id: customerId,
            role: 'customer'
          },
          emailRedirectTo: `${window.location.origin}/login`
        }
      });

      if (authError) throw authError;

      if (authData.user) {
        // Wait for trigger to create profile, then upsert details
        await new Promise(r => setTimeout(r, 800));

        const { error: upsertError } = await supabase
          .from('user_profiles')
          .upsert({
            id: authData.user.id,
            email: form.email,
            full_name: form.full_name,
            phone_number: form.phone_number || null,
            customer_id: customerId,
            company_name: companyName,
            role: 'customer',
            is_super_admin: false
          }, { onConflict: 'id' });

        if (upsertError) console.warn('Profile upsert warning:', upsertError.message);

        // Send password set email to the invited user
        const isProdCustomer = window.location.hostname.includes('traqpack.netlify.app');
        const redirectBase = isProdCustomer ? 'https://traqpack.netlify.app' : window.location.origin;
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(form.email, {
          redirectTo: `${redirectBase}/reset-password`
        });
        if (resetError) console.warn('Could not send password reset email:', resetError.message);
      }

      onNotify?.('success', `Invitation sent to ${form.email}`);
      onSuccess();
      onClose();
    } catch (err: any) {
      onNotify?.('error', err?.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[3000] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Add User</h3>
            <p className="text-xs text-gray-500">User will be added to your organization</p>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-gray-100" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <input
              type="text"
              value={companyName || customerId || ''}
              disabled
              className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">Locked to your organization</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-traqpack-yellow focus:border-transparent"
              placeholder="user@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full name</label>
            <input
              type="text"
              required
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-traqpack-yellow focus:border-transparent"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={form.phone_number}
              onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-traqpack-yellow focus:border-transparent"
              placeholder="+1 (555) 123-4567"
            />
          </div>

          <div className="pt-2 flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-black text-traqpack-yellow rounded-lg text-sm font-semibold hover:bg-gray-900 disabled:opacity-60"
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;


